import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import SimpleButton from '../common/SimpleButton'
import Dropdown from '../common/Dropdown'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import { toast } from '../common/Toast'
import { DownloadModernIcon, ExternalUrlIcon } from '../../icons'
import { getDocuments, getDocumentTypes } from '../../utils/requests/documentsApi'
import { getFileIcon } from '../../utils/functions'
import { handleDocumentDownload } from '../../utils/documentUtils'
import './documents.scss'

const Documents = (props) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { model } = props
  const [documentTypes, setDocumentTypes] = useState([])
  const [documents, setDocuments] = useState([])
  const [selectedType, setSelectedType] = useState(null)
  const [selectedPage, setSelectedPage] = useState(1)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [showSpinner, setShowSpinner] = useState(null)
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  useEffect(() => {
    if (model) {
      getDocumentTypes().then((data) => {
        setDocumentTypes(data.data)
      })
    }
  }, [])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadDocuments(false)
    }
  }, [selectedPage])

  useEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        setSelectedPage(1)
        handleLoadDocuments(true)
      },
      selectedType ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [selectedType])

  const handleLoadDocuments = (withReset) => {
    if (!model) {
      setIsFirstLoad(false)

      return
    }

    let queryParams = {
      model: model,
      page: withReset ? 1 : selectedPage,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    if (selectedType) {
      queryParams = { ...queryParams, document_type: selectedType }
    }

    setShowSpinner(true)

    getDocuments(queryParams)
      .then((data) => {
        if (data?.data) {
          setDocuments(withReset ? data.data : [...documents, ...data.data])
          setCanLoadMore(data.nextPage)
        }
      })
      .catch(() => {
        toast.error(t('error.failure_msg'))

        setCanLoadMore(false)
      })
      .finally(() => {
        setShowSpinner(false)
        setIsFirstLoad(false)
      })
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleNavigateToDocuments = () => {
    history.push({
      pathname: `/documents`,
      state: {
        model: model,
        withBackNavigation: true,
      },
    })
  }

  const selectType = (type) => {
    setSelectedType(type)
  }

  return (
    <div className="products-documents-wrapper">
      <div className="documents-header d-flex">
        <div className="type-wrapper d-flex">
          <div className="type-title">{t('type')}</div>
          <Dropdown
            className="dropdown-input"
            placeholder={t('all')}
            options={documentTypes?.map((documentType) => ({ key: documentType.name, value: documentType.name }))}
            initialSelectedKey={selectedType}
            onOptionReset={() => setSelectedType(null)}
            onChange={(documentType) => selectType(documentType.key)}
          />
        </div>
        <SimpleButton className="action-button" onClick={() => handleNavigateToDocuments()}>
          {t('document_context.view_all_documents')}
        </SimpleButton>
      </div>
      {documents.length > 0 && (
        <>
          <div className="documents-container d-flex">
            <div className="documents-content d-flex">
              {documents.map((item, index) => (
                <div
                  key={index}
                  className={'item-wrapper' + (index === documents.length - 1 ? ' no-border' : '')}
                  onClick={() => {
                    handleDocumentDownload(item)
                  }}
                >
                  <div className="image-wrapper movable-image">
                    <DownloadModernIcon />
                  </div>
                  <div className="image-wrapper rounded-image">{item.is_external_document ? <ExternalUrlIcon /> : getFileIcon(item.file_name)}</div>
                  <div className="name-wrapper">
                    <p className="title">{item?.document_title ? item?.document_title : item?.file_name}</p>
                    <p className="section">{item?.section_name}</p>
                  </div>
                </div>
              ))}
            </div>
            {canLoadMore && (
              <div className="buttons-wrapper">
                <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
                  {t('load_more')}
                </SimpleButton>
              </div>
            )}
          </div>
        </>
      )}
      {!isFirstLoad && documents.length === 0 && <EmptyPageResults className="empty-list" title={t('no_data_available')} />}
      {showSpinner && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

export default Documents
